import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  EMPTY_BG_SCAN_BULK_FORM,
  formatBgScanChannelList,
  hasBgScanBulkFormChanges,
} from '../utils/bgScanPatchUtils';
import { collectApChannelsByBandFromDevices } from '../utils/channelMapUtils';

const ON_OFF_EMPTY = [
  { value: '', label: '(unchanged)' },
  { value: 'on', label: 'on' },
  { value: 'off', label: 'off' },
];

const BackgroundStaScanBulkPanel = ({
  nodeCount = 0,
  capCount = 0,
  selectedDevices = [],
  form = EMPTY_BG_SCAN_BULK_FORM,
  onChange,
  onApply,
  loading = false,
}) => {
  const setField = (key, value) => {
    onChange?.({ ...form, [key]: value });
  };

  const apChannelsByBand = useMemo(
    () => collectApChannelsByBandFromDevices(selectedDevices),
    [selectedDevices],
  );
  const apChannelCount =
    apChannelsByBand['5GHz'].length + apChannelsByBand['6GHz'].length;

  const fillChannelGroupsFromSelection = () => {
    onChange?.({
      ...form,
      channels5GHz: formatBgScanChannelList(apChannelsByBand['5GHz']),
      channels6GHz: formatBgScanChannelList(apChannelsByBand['6GHz']),
      clearChannelGroups: false,
    });
  };

  const hasChanges = hasBgScanBulkFormChanges(form);
  const canApply = nodeCount > 0 && hasChanges && !loading;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Background STA scan
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Apply the same <code>background_sta_scan</code> settings to all selected Node devices
          in one request (<code>PATCH /tree-mgmt/devices/config</code>). Empty fields are left
          unchanged. When you set ChannelGroups for a band, the whole ChannelGroups object on
          each device is replaced (other bands are dropped unless you list them too).
        </Typography>

        {capCount > 0 && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            {capCount} CAP device{capCount === 1 ? '' : 's'} in the selection will be skipped
            (background scan applies to Node devices only).
          </Typography>
        )}

        {nodeCount === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select at least one Node device to update background scan settings.
          </Typography>
        )}

        <Stack spacing={2.5}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>BG scan on/off</InputLabel>
              <Select
                label="BG scan on/off"
                value={form.bgScanOnOff}
                displayEmpty
                onChange={(e) => setField('bgScanOnOff', e.target.value)}
              >
                {ON_OFF_EMPTY.map((opt) => (
                  <MenuItem key={opt.value || 'unchanged'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel shrink>Fast recovery</InputLabel>
              <Select
                label="Fast recovery"
                value={form.fastRoamingOnOff}
                displayEmpty
                onChange={(e) => setField('fastRoamingOnOff', e.target.value)}
              >
                {ON_OFF_EMPTY.map((opt) => (
                  <MenuItem key={`fr-${opt.value || 'unchanged'}`} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="number"
              label="Channel scan interval (s)"
              placeholder="e.g. 15"
              value={form.chanScanInterval}
              onChange={(e) => setField('chanScanInterval', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              type="number"
              label="Scan interval (s)"
              placeholder="e.g. 3600"
              value={form.scanInterval}
              onChange={(e) => setField('scanInterval', e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label="Self improvement"
              placeholder="e.g. 4 or 2147483647"
              value={form.selfImprovement}
              onChange={(e) => setField('selfImprovement', e.target.value)}
              fullWidth
            />
          </Box>

          <Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ mb: 1.5 }}
            >
              <Button
                variant="outlined"
                size="small"
                disabled={selectedDevices.length === 0 || apChannelCount === 0}
                onClick={fillChannelGroupsFromSelection}
              >
                Use AP channels from selection
              </Button>
              <Typography variant="caption" color="text.secondary">
                {selectedDevices.length === 0
                  ? 'Select devices on the map to detect AP channels.'
                  : apChannelCount === 0
                    ? 'No 5/6 GHz AP channels found on the selected devices.'
                    : `${apChannelsByBand['5GHz'].length} on 5GHz${
                        apChannelsByBand['6GHz'].length > 0
                          ? `, ${apChannelsByBand['6GHz'].length} on 6GHz`
                          : ''
                      } from ${selectedDevices.length} selected device${
                        selectedDevices.length === 1 ? '' : 's'
                      }`}
              </Typography>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                size="small"
                label="ChannelGroups — 5GHz"
                placeholder="e.g. 36, 149, 56"
                helperText={
                  apChannelsByBand['5GHz'].length > 0
                    ? `Detected: ${formatBgScanChannelList(apChannelsByBand['5GHz'])}`
                    : 'Comma-separated; replaces 5GHz list when set'
                }
                value={form.channels5GHz}
                onChange={(e) => setField('channels5GHz', e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="ChannelGroups — 6GHz"
                placeholder="e.g. 1, 5, 9"
                helperText={
                  apChannelsByBand['6GHz'].length > 0
                    ? `Detected: ${formatBgScanChannelList(apChannelsByBand['6GHz'])}`
                    : 'Comma-separated; replaces 6GHz list when set'
                }
                value={form.channels6GHz}
                onChange={(e) => setField('channels6GHz', e.target.value)}
                fullWidth
              />
            </Box>
          </Box>

          <FormControlLabel
            control={(
              <Checkbox
                checked={!!form.clearChannelGroups}
                onChange={(e) => setField('clearChannelGroups', e.target.checked)}
              />
            )}
            label="Clear ChannelGroups (unrestricted scan)"
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={!canApply}
            onClick={onApply}
            sx={{ py: 1.5, mt: 1 }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Applying…' : `Apply to ${nodeCount} Node${nodeCount === 1 ? '' : 's'}`}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BackgroundStaScanBulkPanel;
